import { c as _c } from "react/compiler-runtime";
import { useId, type DragEvent } from "react";
import { KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import type { ImageItem } from "./ImagesInput";
type Args = {
  value: ImageItem[];
  onChange: (next: ImageItem[]) => void;
  disabled?: boolean;
  canAdd: boolean;
  maxImages: number;
};
export function useImagesInput(t0) {
  const $ = _c(40);
  const {
    value,
    onChange,
    disabled,
    canAdd,
    maxImages
  } = t0;
  const uploadId = useId();
  const interactive = !disabled;
  const canUpload = interactive && canAdd;
  let t1;
  if ($[0] !== value) {
    t1 = value.filter(_temp);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const usableCount = t1.length;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      activationConstraint: {
        distance: 5
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      activationConstraint: {
        delay: 250,
        tolerance: 5
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      coordinateGetter: sortableKeyboardCoordinates
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const sensors = useSensors(useSensor(MouseSensor, t2), useSensor(TouchSensor, t3), useSensor(KeyboardSensor, t4));
  let t5;
  if ($[5] !== interactive || $[6] !== onChange || $[7] !== value) {
    t5 = event => {
      if (!interactive) {
        return;
      }
      const {
        active,
        over
      } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = value.findIndex(item_0 => item_0.id === active.id);
      const newIndex = value.findIndex(item_1 => item_1.id === over.id);
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      onChange(arrayMove(value, oldIndex, newIndex));
    };
    $[5] = interactive;
    $[6] = onChange;
    $[7] = value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleDragEnd = t5;
  let t6;
  if ($[9] !== onChange || $[10] !== value) {
    t6 = id => {
      const item_2 = value.find(i => i.id === id);
      if (!item_2) {
        return;
      }
      if (item_2.file) {
        if (item_2.url.startsWith("blob:")) {
          URL.revokeObjectURL(item_2.url);
        }
        onChange(value.filter(i_0 => i_0.id !== id));
      } else {
        onChange(value.map(i_1 => i_1.id === id ? {
          ...i_1,
          pendingDeletion: true
        } : i_1));
      }
    };
    $[9] = onChange;
    $[10] = value;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleRemove = t6;
  let t7;
  if ($[12] !== onChange || $[13] !== value) {
    t7 = id_0 => {
      onChange(value.map(i_2 => i_2.id === id_0 ? {
        ...i_2,
        pendingDeletion: false
      } : i_2));
    };
    $[12] = onChange;
    $[13] = value;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleRestore = t7;
  let t8;
  if ($[15] !== onChange || $[16] !== value) {
    t8 = () => {
      onChange(value.filter(_temp2).map(_temp3));
    };
    $[15] = onChange;
    $[16] = value;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleClearAll = t8;
  let t9;
  if ($[18] !== interactive || $[19] !== maxImages || $[20] !== onChange || $[21] !== usableCount || $[22] !== value) {
    t9 = incoming => {
      if (!interactive) {
        return;
      }
      const remaining = Math.max(0, maxImages - usableCount);
      if (remaining === 0) {
        return;
      }
      const next = incoming.slice(0, remaining).map(_temp4);
      onChange([...value, ...next]);
    };
    $[18] = interactive;
    $[19] = maxImages;
    $[20] = onChange;
    $[21] = usableCount;
    $[22] = value;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const addFiles = t9;
  let t10;
  if ($[24] !== addFiles || $[25] !== canUpload || $[26] !== uploadId) {
    t10 = canUpload ? {
      onClick: () => document.getElementById(uploadId)?.click(),
      onDragOver: _temp5,
      onDragLeave: _temp6,
      onDrop: e_1 => {
        e_1.preventDefault();
        e_1.stopPropagation();
        e_1.currentTarget.classList.remove("bg-blue-100");
        if (e_1.dataTransfer.files?.length) {
          addFiles(Array.from(e_1.dataTransfer.files));
        }
      }
    } : {};
    $[24] = addFiles;
    $[25] = canUpload;
    $[26] = uploadId;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const dropHandlers = t10;
  let t11;
  if ($[28] !== addFiles || $[29] !== canUpload || $[30] !== dropHandlers || $[31] !== handleClearAll || $[32] !== handleDragEnd || $[33] !== handleRemove || $[34] !== handleRestore || $[35] !== interactive || $[36] !== sensors || $[37] !== uploadId || $[38] !== usableCount) {
    t11 = {
      uploadId,
      interactive,
      canUpload,
      usableCount,
      sensors,
      dropHandlers,
      handleDragEnd,
      handleRemove,
      handleRestore,
      handleClearAll,
      addFiles
    };
    $[28] = addFiles;
    $[29] = canUpload;
    $[30] = dropHandlers;
    $[31] = handleClearAll;
    $[32] = handleDragEnd;
    $[33] = handleRemove;
    $[34] = handleRestore;
    $[35] = interactive;
    $[36] = sensors;
    $[37] = uploadId;
    $[38] = usableCount;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  return t11;
}
function _temp6(e_0) {
  e_0.preventDefault();
  e_0.stopPropagation();
  e_0.currentTarget.classList.remove("bg-blue-100");
}
function _temp5(e) {
  e.preventDefault();
  e.stopPropagation();
  e.currentTarget.classList.add("bg-blue-100");
}
function _temp4(file) {
  return {
    id: crypto.randomUUID(),
    url: URL.createObjectURL(file),
    size: file.size,
    file
  };
}
function _temp3(i_4) {
  return {
    ...i_4,
    pendingDeletion: true
  };
}
function _temp2(i_3) {
  return !i_3.file;
}
function _temp(item) {
  return !item.pendingDeletion;
}